"use client";

import type { ReactNode, CSSProperties } from "react";
import { ArrowRight, Phone, Mail, MapPin, FileText, ShieldCheck, Users } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
const jpSerif: CSSProperties = {
  fontFamily: '"Playfair Display", "Hiragino Mincho ProN", "Yu Mincho", serif'
};
const COLORS = {
  NAVY: "hsl(215 50% 15%)",
  CHARCOAL: "hsl(210 10% 23%)",
  GOLD: "hsl(43 35% 60%)",
  IVORY: "hsl(30 10% 97%)",
  PARCHMENT: "hsl(30 15% 94%)",
  WHITE: "hsl(0 0% 100%)",
  DEEP_CHARCOAL: "hsl(215 50% 8%)"
};
const TARGET_AUDIENCE = [{
  id: 1,
  text: "フロリダで不動産の取得・売却・所有に関する法的確認が必要な方"
}, {
  id: 2,
  text: "フロリダ州の LLC・会社・事業関係について整理したい方"
}, {
  id: 3,
  text: "米国資産を含む相続・信託・プロベートについて相談したい方"
}, {
  id: 4,
  text: "契約書、所有権、クロージング関連事項について弁護士の確認が必要な方"
}, {
  id: 5,
  text: "日本の弁護士・税理士・会計士・不動産関係者から紹介を受けた方"
}];
const PRACTICES = [{
  num: "01",
  title: "法人設立・所有権構成",
  sub: "Florida business formation / ownership structure",
  body: "フロリダ州における法人設立、所有権構成、事業の取得・売却、関連契約書類について法的確認を行います。"
}, {
  num: "02",
  title: "不動産取引・所有権移転",
  sub: "Real estate transactions / ownership transfer",
  body: "南フロリダにおける住宅・商業不動産の取引、所有権移転、契約書、クロージング関連事項について法的確認を行います。"
}, {
  num: "03",
  title: "遺言・信託・フロリダ資産の承継",
  sub: "Wills, trusts, and Florida asset succession",
  body: "フロリダ州に所在する資産や事業の承継に備え、遺言、信託、委任状、ヘルスケア関連書類の作成および法的確認を行います。"
}, {
  num: "04",
  title: "プロベート・信託管理",
  sub: "Probate and trust administration",
  body: "フロリダ州のプロベート手続および信託管理について、Personal Representative、Trustee、受益者などに関わる法的事項に対応します。"
}, {
  num: "05",
  title: "弁護士費用審理・専門家証言",
  sub: "Attorney’s fee expert testimony",
  body: "弁護士費用・コスト請求について、費やされた時間、作業内容、請求額等の合理性を検討し、必要に応じて専門家としての意見・宣誓供述書等に対応します。"
}, {
  num: "06",
  title: "民事訴訟・ビジネス紛争",
  sub: "CIVIL LITIGATION / BUSINESS AND COMMERCIAL DISPUTES",
  body: "当事務所は、事業、契約、不動産関連、所有権、パートナーシップ、会社関係に関する民事訴訟および商事紛争に対応します。訴訟、訴訟前の交渉、請求・防御、和解交渉、裁判所での手続を含め、事案ごとの事実関係、証拠、リスク、費用対効果を踏まえた実務的な対応を重視しています。"
}];
const STEPS = [{
  num: "01",
  title: "初期連絡・書類整理",
  body: "案件の概要、関係者、関連書類、期限などを日本語で整理します。"
}, {
  num: "02",
  title: "当事務所の弁護士による確認",
  body: "当事務所の弁護士が内容を確認し、法的論点と対応可能性を検討します。"
}, {
  num: "03",
  title: "法的対応",
  body: "委任契約の締結後、契約書レビュー、書類作成、訴訟対応など必要な法的業務を行います。"
}];
const NOTICES = [{
  id: 1,
  text: "日本語での初期連絡、情報整理、書類整理は、それ自体が法的助言または法的代理を意味するものではありません。"
}, {
  id: 2,
  text: "法的助言および法律業務は、当事務所が受任可能性を確認し、委任契約を締結した後、当事務所の弁護士が行います。"
}, {
  id: 3,
  text: "ウェブサイトからの連絡や情報送信のみでは、弁護士と依頼人の関係は成立しません。"
}];
function HeroSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="jp-hero" id="jp-hero" className="relative flex min-h-[90vh] items-center overflow-hidden px-6 py-32" style={{
    background: COLORS.NAVY
  }}>
      <div className={`mx-auto w-full max-w-[900px] transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} ref={ref}>
        <p className="mb-8 text-[16px] font-medium uppercase tracking-[0.3em]" style={{
        color: COLORS.GOLD
      }}>JAPANESE-LANGUAGE INITIAL CONTACT &amp; DOCUMENT ORGANIZATION</p>
        
        <h1 className="mb-10 text-[35px] font-normal leading-[1.2] md:text-[50px]" style={{
        ...jpSerif,
        color: COLORS.IVORY
      }}>日本語での初期連絡をご希望の方へ</h1>
        
        <p className="mb-12 text-[24px] font-normal leading-[1.85] md:text-[28px]" style={{
        color: COLORS.IVORY
      }}>フロリダでのビジネス、不動産取引、遺言・信託・プロベート、弁護士費用などに関するご相談について、日本語での初期連絡、情報整理、書類整理に対応しています。</p>

        <div className="mb-16 space-y-8 text-[22px] leading-[2.1] text-white/80">
          
          
          <div className="border-l-2 pl-8" style={{
          borderColor: COLORS.GOLD
        }}>
            <p className="font-medium text-white">案件の概要、関係者、関連書類、期限などを日本語で整理し、当事務所の弁護士による確認につなげます。</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <Link to="/InitialContactInJapanesePage">
            <Button size="lg" className="h-16 rounded-lg bg-[hsl(43,35%,60%)] px-10 text-[20px] font-medium text-white hover:bg-[hsl(43,35%,55%)]">
              日本語で初期連絡をする
              <ArrowRight className="ml-2 size-5" />
            </Button>
          </Link>
          <Link to="#practice-areas">
            
          </Link>
        </div>
      </div>
    </section>;
}
function AudienceSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}
function MissionSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}
function PracticeAreasSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section id="practice-areas" className="px-6 py-32" style={{
    backgroundColor: COLORS.WHITE
  }}>
      <div ref={ref} className={`mx-auto max-w-[900px] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="mb-16">
          <p className="mb-4 text-[20px] font-medium uppercase tracking-[0.25em]" style={{
          color: COLORS.GOLD
        }}>
            Practice Areas
          </p>
          <h2 className="mb-6 text-[40px] font-normal leading-[1.3] md:text-[42px]" style={{
          ...jpSerif,
          color: COLORS.CHARCOAL
        }}>
            対応分野
          </h2>
          <div className="h-[2px] w-16" style={{
          backgroundColor: COLORS.GOLD
        }} />
        </div>
        
        <div className="mt-16 space-y-0">
          {PRACTICES.map((p, i) => <div key={p.num} data-index={i} className="group relative border-t border-border py-16 last:border-b">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-[100px_1fr]">
                <span className="text-[24px] font-medium tracking-widest" style={{
              color: COLORS.GOLD
            }}>{p.num}</span>
                <div>
                  <div className="mb-4">
                    <h3 className="mb-2 text-[32px] font-normal text-foreground" style={jpSerif}>{p.title}</h3>
                    
                  </div>
                  <p className="text-[22px] leading-[1.85] text-muted-foreground">{p.body}</p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function ProcessSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="jp-process" className="px-6 py-32" style={{
    backgroundColor: COLORS.NAVY
  }}>
      <div ref={ref} className={`mx-auto max-w-[900px] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="mb-16">
          <p className="mb-4 text-[20px] font-medium uppercase tracking-[0.25em]" style={{
          color: COLORS.GOLD
        }}>
            Process
          </p>
          <h2 className="mb-6 text-[40px] font-normal leading-[1.3] md:text-[42px]" style={{
          ...jpSerif,
          color: COLORS.IVORY
        }}>
            ご相談の流れ
          </h2>
          <div className="h-[2px] w-16" style={{
          backgroundColor: COLORS.GOLD
        }} />
        </div>
        
        

        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          {STEPS.map((step, i) => <div key={step.num} data-index={i} className="relative flex flex-col gap-6 rounded-lg border border-white/10 bg-white/5 p-10">
              <div className="text-[20px] font-medium tracking-widest" style={{
            color: COLORS.GOLD
          }}>Step {step.num}</div>
              <h3 className="text-[28px] font-normal leading-tight text-white" style={jpSerif}>{step.title}</h3>
              <p className="text-[20px] leading-[1.85] text-white/60">{step.body}</p>
            </div>)}
        </div>
      </div>
    </section>;
}
function ReferralsSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="professional-referrals" className="px-6 py-32" style={{
    backgroundColor: COLORS.IVORY
  }}>
      <div ref={ref} className={`mx-auto max-w-[900px] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="mb-16">
          <p className="mb-4 text-[20px] font-medium uppercase tracking-[0.25em]" style={{
          color: COLORS.GOLD
        }}>FOR PROFESSIONALS</p>
          <h2 className="mb-6 text-[40px] font-normal leading-[1.3] md:text-[42px]" style={{
          ...jpSerif,
          color: COLORS.CHARCOAL
        }}>専門家の方へ</h2>
          <div className="h-[2px] w-16" style={{
          backgroundColor: COLORS.GOLD
        }} />
        </div>
        
        <div className="rounded-lg border border-border bg-white p-12 shadow-sm">
          <div className="mb-12 space-y-8 text-[22px] leading-[2.1] text-muted-foreground">
            <p><div data-ast-id="f:/src/pages/JapaneseLanguageSupport/sections/PracticeAreasOverview.tsx|sig:vhadzv" data-ast-start="14018" data-ast-end="14267">日本または米国の弁護士、会計士、税理士、不動産関係者など、フロリダ法に関する法的確認が必要な案件について、専門家の方からのお問い合わせにも対応しています。</div></p>
            
          </div>
          <Link to="#japanese-contact">
            
          </Link>
        </div>
      </div>
    </section>;
}
function NoticesSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="jp-notices" className="px-6 py-32" style={{
    backgroundColor: COLORS.DEEP_CHARCOAL
  }}>
      <div ref={ref} className={`mx-auto max-w-[900px] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="mb-16">
          <p className="mb-4 text-[20px] font-medium uppercase tracking-[0.25em]" style={{
          color: COLORS.GOLD
        }}>
            Important Notices
          </p>
          <h2 className="mb-6 text-[40px] font-normal leading-[1.3] md:text-[42px]" style={{
          ...jpSerif,
          color: COLORS.IVORY
        }}>
            重要事項
          </h2>
          <div className="h-[2px] w-16" style={{
          backgroundColor: COLORS.GOLD
        }} />
        </div>
        
        <div className="divide-y divide-white/10 rounded-lg border border-white/10 bg-white/5 p-12">
          {NOTICES.map((notice, i) => <div key={notice.id} data-index={i} className="flex items-start gap-6 py-8 first:pt-0 last:pb-0">
              <div className="mt-3 size-2 shrink-0 rounded-lg" style={{
            backgroundColor: COLORS.GOLD
          }} />
              <p className="text-[22px] leading-[1.85] text-white/70">{notice.text}</p>
            </div>)}
        </div>
      </div>
    </section>;
}
function ContactSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}
export default function JapaneseLanguageSupport() {
  return <main className="min-h-screen" style={{
    backgroundColor: COLORS.WHITE
  }}>
      <HeroSection />
      <AudienceSection />
      <MissionSection />
      <PracticeAreasSection />
      <ProcessSection />
      <ReferralsSection />
      <NoticesSection />
      <ContactSection />
    </main>;
}